/**
 * PollBlock — Web renderer for POLL content blocks.
 *
 * Displays poll question and options as a read-only preview.
 * Vote submission is disabled until the backend vote persistence
 * endpoint is wired — prevents collecting votes that would be
 * silently discarded on page reload.
 */

import "./PollBlock.scss";

/** Validates that a URL uses a safe protocol before rendering in <img src> */
const isSafeImageUrl = (url?: string) => !!url && (/^https?:\/\//).test(url);

interface PollOption {
    _id?: string;
    label: string;
    imageUrl?: string;
}

interface SurfacePollConfigResponse {
    question: string;
    options?: PollOption[];
    allowMultiple?: boolean;
    showResults?: boolean;
}

interface PollBlockProps {
    config?: SurfacePollConfigResponse;
    sectionTitle?: string;
}

export const PollBlock = ({ config, sectionTitle }: PollBlockProps) => {
    const options = config?.options || [];
    const question = config?.question || "";
    const allowMultiple = config?.allowMultiple || false;

    if (!question || options.length === 0) return null;

    return (
        <div className="poll-block">
            {sectionTitle && <h2 className="section-title">{sectionTitle}</h2>}
            <p className="poll-block__question">{question}</p>
            <div className="poll-block__options">
                {options.map((opt, i) => {
                    const key = opt._id || String(i);
                    return (
                        <div
                            key={key}
                            className="poll-block__option poll-block__option--preview"
                        >
                            <span className={`poll-block__indicator ${allowMultiple ? "poll-block__indicator--checkbox" : "poll-block__indicator--radio"}`}>
                                <span className="poll-block__indicator-dot" />
                            </span>
                            {isSafeImageUrl(opt.imageUrl) &&
                                <img
                                    src={opt.imageUrl}
                                    alt={opt.label}
                                    className="poll-block__option-image"
                                    loading="lazy"
                                />
                            }
                            <span className="poll-block__option-label">{opt.label}</span>
                        </div>
                    );
                })}
            </div>
            <p className="poll-block__coming-soon">Voting coming soon</p>
        </div>
    );
};
